import React, { useState, useEffect } from 'react';
import PageLayout from '../../components/PageLayout';
import Modal from '../../components/Modal';
import Toast from '../../components/Toast';
import HedgeAddEdit from '../../components/AddEditItems/HedgeAddEdit';
import { getLoginDetails, canAccessHedgeManagement } from '../../utils/storage';
import HeaderIconSvg from '../../icons/newicons/arrow-down-up.svg';
import {
  GET_HEDGES_URL,
  ADD_HEDGE_URL,
  EDIT_HEDGE_URL,
  UPDATE_HEDGE_STATUS_URL,
  GET_HEDGE_DASHBOARD_URL,
  getData,
  postData,
  putData,
} from '../../utils/api';
import { Link } from 'react-router-dom';
import './hedge.css';

const hedgeModel = {
  hedgeId: undefined,
  oemName: '',
  hedgeDate: '',
  abcatLotId: '',
  oemLotId: '',
  ptOunces: '',
  ptPricePerOz: '',
  pdOunces: '',
  pdPricePerOz: '',
  rhOunces: '',
  rhPricePerOz: '',
  notes: '',
};

const Hedge = () => {
  const loginDetails = getLoginDetails();
  const canAccess = canAccessHedgeManagement(loginDetails);
  const [hedges, setHedges] = useState([]);
  const [isAddPopupShown, setIsAddPopupShown] = useState(false);
  const [isEditPopupShown, setIsEditPopupShown] = useState(false);
  const [selectedHedge, setSelectedHedge] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    totalHedged: { pt: 0, pd: 0, rh: 0 },
    totalOutstanding: { pt: 0, pd: 0, rh: 0 },
    difference: { pt: 0, pd: 0, rh: 0 },
  });
  const [dateRange, setDateRange] = useState({
    fromDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0], // Last 30 days
    toDate: new Date().toISOString().split('T')[0], // Today
  });
  const [toastParams, setToastParams] = useState({
    message: '',
    type: '',
    id: '',
  });
  const [params, setParams] = useState({
    limit: 50,
    offset: 0,
    areMoreItemsAvailable: false,
  });
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    if (!canAccess) {
      window.location.href = '/';
    }
  }, [canAccess]);

  useEffect(() => {
    if (canAccess) {
      fetchHedges();
      fetchDashboardData();
    }
  }, [canAccess, showInactive]);

  useEffect(() => {
    if (canAccess && dateRange.fromDate && dateRange.toDate) {
      fetchDashboardData();
    }
  }, [dateRange, canAccess]);

  const fetchHedges = async () => {
    try {
      setIsLoading(true);
      const data = await getData(GET_HEDGES_URL, {
        count: params.limit,
        offset: params.offset,
        includeInactive: showInactive,
      });
      setHedges(data.list || []);
      setParams({
        ...params,
        areMoreItemsAvailable: data.areMoreItemsAvailable || false,
      });
      setIsLoading(false);
    } catch (e) {
      console.log('ERROR fetching hedges', e);
      setHedges([]);
      setParams({
        ...params,
        areMoreItemsAvailable: false,
      });
      setToastParams({
        type: 'ERROR',
        message: e && e.error ? e.error : 'Failed to load hedges',
        id: new Date().getTime(),
      });
      setIsLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const data = await getData(GET_HEDGE_DASHBOARD_URL, {
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate,
        customerId: '',
        buyerId: '',
      });
      setDashboardData({
        totalHedged: {
          pt: data.totalHedged?.pt || 0,
          pd: data.totalHedged?.pd || 0,
          rh: data.totalHedged?.rh || 0,
        },
        totalOutstanding: {
          pt: data.totalOutstanding?.pt || 0,
          pd: data.totalOutstanding?.pd || 0,
          rh: data.totalOutstanding?.rh || 0,
        },
        difference: {
          pt: (data.totalHedged?.pt || 0) - (data.totalOutstanding?.pt || 0),
          pd: (data.totalHedged?.pd || 0) - (data.totalOutstanding?.pd || 0),
          rh: (data.totalHedged?.rh || 0) - (data.totalOutstanding?.rh || 0),
        },
      });
    } catch (e) {
      console.log('ERROR fetching dashboard data', e);
      setDashboardData({
        totalHedged: { pt: 0, pd: 0, rh: 0 },
        totalOutstanding: { pt: 0, pd: 0, rh: 0 },
        difference: { pt: 0, pd: 0, rh: 0 },
      });
    }
  };

  const handleAdd = () => {
    setSelectedHedge({ ...hedgeModel });
    setIsAddPopupShown(true);
  };

  const handleEdit = (hedge) => {
    setSelectedHedge(hedge);
    setIsEditPopupShown(true);
  };

  const handleToggleStatus = async (hedge) => {
    const isActive = hedge.status !== 'CANCELLED';
    const nextStatus = isActive ? 'CANCELLED' : 'ACTIVE';
    const actionLabel = isActive ? 'deactivate' : 'reactivate';

    if (!window.confirm(`Are you sure you want to ${actionLabel} this hedge contract?`)) {
      return;
    }

    try {
      setIsLoading(true);
      await putData(UPDATE_HEDGE_STATUS_URL, {
        hedgeId: hedge.hedgeId,
        status: nextStatus,
      });
      setToastParams({
        type: 'SUCCESS',
        message: isActive ? 'Hedge contract deactivated' : 'Hedge contract reactivated',
        id: new Date().getTime(),
      });
      fetchHedges();
      fetchDashboardData();
      setIsLoading(false);
    } catch (e) {
      console.log('ERROR updating hedge status', e);
      setToastParams({
        type: 'ERROR',
        message: e && e.error ? e.error : 'Failed to update hedge status',
        id: new Date().getTime(),
      });
      setIsLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      setIsLoading(true);
      if (data.hedgeId) {
        await putData(EDIT_HEDGE_URL, data);
        setToastParams({
          type: 'SUCCESS',
          message: 'Hedge updated successfully',
          id: new Date().getTime(),
        });
      } else {
        await postData(ADD_HEDGE_URL, data);
        setToastParams({
          type: 'SUCCESS',
          message: 'Hedge added successfully',
          id: new Date().getTime(),
        });
      }
      setIsAddPopupShown(false);
      setIsEditPopupShown(false);
      setSelectedHedge(null);
      fetchHedges();
      fetchDashboardData();
      setIsLoading(false);
    } catch (e) {
      console.log('ERROR saving hedge', e);
      setToastParams({
        type: 'ERROR',
        message: e && e.error ? e.error : 'Failed to save hedge',
        id: new Date().getTime(),
      });
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsAddPopupShown(false);
    setIsEditPopupShown(false);
    setSelectedHedge(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0.00';
    return parseFloat(num).toFixed(2);
  };

  const formatCurrency = (num) => {
    if (!num && num !== 0) return '0.00';
    return parseFloat(num).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const renderMetalBlock = (label, ouncesDisplay, priceDisplay, totalValue) => (
    <div className="hedge-metal-card">
      <div className="hedge-metal-label">{label}</div>
      <div className="hedge-metal-amount">
        {ouncesDisplay}
        <span className="hedge-metal-unit">oz</span>
      </div>
      {priceDisplay && parseFloat(priceDisplay) > 0 && (
        <>
          <div className="hedge-metal-price">@ ${priceDisplay}/oz</div>
          <div className="hedge-metal-total">${formatCurrency(totalValue)}</div>
        </>
      )}
    </div>
  );

  if (!canAccess) {
    return null;
  }

  const hedgeItems = hedges.map((hedge) => {
    const ptTotal = (hedge.ptOunces || 0) * (hedge.ptPricePerOz || 0);
    const pdTotal = (hedge.pdOunces || 0) * (hedge.pdPricePerOz || 0);
    const rhTotal = (hedge.rhOunces || 0) * (hedge.rhPricePerOz || 0);
    const grandTotal = ptTotal + pdTotal + rhTotal;

    return {
      ...hedge,
      isActive: hedge.status !== 'CANCELLED',
      hedgeDateDisplay: formatDate(hedge.hedgeDate),
      ptOuncesDisplay: formatNumber(hedge.ptOunces),
      ptPricePerOzDisplay: formatNumber(hedge.ptPricePerOz),
      ptTotalValue: formatNumber(ptTotal),
      pdOuncesDisplay: formatNumber(hedge.pdOunces),
      pdPricePerOzDisplay: formatNumber(hedge.pdPricePerOz),
      pdTotalValue: formatNumber(pdTotal),
      rhOuncesDisplay: formatNumber(hedge.rhOunces),
      rhPricePerOzDisplay: formatNumber(hedge.rhPricePerOz),
      rhTotalValue: formatNumber(rhTotal),
      grandTotalValue: formatNumber(grandTotal),
    };
  });

  return (
    <PageLayout
      icon={HeaderIconSvg}
      heading="Hedge Management"
      searchPlaceholder=""
      hideSearchBar={true}
    >
      <div className="hedge-page-content">
      <div className="hedge-filters-card">
        <div className="hedge-filter-row">
          <div className="hedge-filter-date-field">
            <span className="hedge-filter-label">From:</span>
            <input
              type="date"
              className="hedge-filter-date-input"
              value={dateRange.fromDate}
              onChange={(e) => setDateRange({ ...dateRange, fromDate: e.target.value })}
            />
          </div>
          <div className="hedge-filter-date-field">
            <span className="hedge-filter-label">To:</span>
            <input
              type="date"
              className="hedge-filter-date-input"
              value={dateRange.toDate}
              onChange={(e) => setDateRange({ ...dateRange, toDate: e.target.value })}
            />
          </div>

          <div className="hedge-filter-presets">
            <button
              type="button"
              className="hedge-filter-preset-btn"
              onClick={() => {
                const today = new Date();
                const last30Days = new Date(today);
                last30Days.setDate(today.getDate() - 30);
                setDateRange({
                  fromDate: last30Days.toISOString().split('T')[0],
                  toDate: today.toISOString().split('T')[0],
                });
              }}
            >
              30 Days
            </button>
            <button
              type="button"
              className="hedge-filter-preset-btn"
              onClick={() => {
                const today = new Date();
                const last90Days = new Date(today);
                last90Days.setDate(today.getDate() - 90);
                setDateRange({
                  fromDate: last90Days.toISOString().split('T')[0],
                  toDate: today.toISOString().split('T')[0],
                });
              }}
            >
              90 Days
            </button>
            <button
              type="button"
              className="hedge-filter-preset-btn"
              onClick={() => {
                const today = new Date();
                const yearStart = new Date(today.getFullYear(), 0, 1);
                setDateRange({
                  fromDate: yearStart.toISOString().split('T')[0],
                  toDate: today.toISOString().split('T')[0],
                });
              }}
            >
              YTD
            </button>
          </div>

          <div className="hedge-filter-actions">
            <label className="hedge-show-inactive">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <span>Show inactive</span>
            </label>
            <Link to="/insights/hedge-planning" className="hedge-toolbar-btn hedge-toolbar-btn--outline">
              Hedge Planning
            </Link>
            <button
              type="button"
              onClick={handleAdd}
              className="hedge-toolbar-btn hedge-toolbar-btn--primary hedge-toolbar-btn--add"
              title="Add Hedge"
              aria-label="Add Hedge"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                fill="currentColor"
                viewBox="0 0 16 16"
                aria-hidden="true"
              >
                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
              </svg>
            </button>
          </div>
        </div>
        <div className="hedge-filter-hint">
          Outstanding ounces: converters bought & processed within selected date range
        </div>
      </div>

      {/* Dashboard Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '20px',
          marginBottom: '24px',
        }}
      >
        {/* Total Hedged Card */}
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            padding: '20px',
            border: '1px solid #e9ecef',
          }}
        >
          <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '12px', fontWeight: '500' }}>
            Total Hedged Ounces
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#495057' }}>PT:</span>
              <span style={{ fontSize: '16px', fontWeight: '700', color: '#212529', fontFamily: 'monospace' }}>
                {formatNumber(dashboardData.totalHedged.pt)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#495057' }}>PD:</span>
              <span style={{ fontSize: '16px', fontWeight: '700', color: '#212529', fontFamily: 'monospace' }}>
                {formatNumber(dashboardData.totalHedged.pd)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#495057' }}>RH:</span>
              <span style={{ fontSize: '16px', fontWeight: '700', color: '#212529', fontFamily: 'monospace' }}>
                {formatNumber(dashboardData.totalHedged.rh)}
              </span>
            </div>
          </div>
        </div>

        {/* Total Outstanding Card */}
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            padding: '20px',
            border: '1px solid #e9ecef',
          }}
        >
          <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '8px', fontWeight: '500' }}>
            Total Outstanding Ounces
          </div>
          <div style={{ fontSize: '11px', color: '#adb5bd', marginBottom: '12px' }}>
            ({formatDate(dateRange.fromDate)} - {formatDate(dateRange.toDate)})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#495057' }}>PT:</span>
              <span style={{ fontSize: '16px', fontWeight: '700', color: '#dc3545', fontFamily: 'monospace' }}>
                {formatNumber(dashboardData.totalOutstanding.pt)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#495057' }}>PD:</span>
              <span style={{ fontSize: '16px', fontWeight: '700', color: '#dc3545', fontFamily: 'monospace' }}>
                {formatNumber(dashboardData.totalOutstanding.pd)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#495057' }}>RH:</span>
              <span style={{ fontSize: '16px', fontWeight: '700', color: '#dc3545', fontFamily: 'monospace' }}>
                {formatNumber(dashboardData.totalOutstanding.rh)}
              </span>
            </div>
          </div>
        </div>

        {/* Difference Card */}
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            padding: '20px',
            border: '1px solid #e9ecef',
          }}
        >
          <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '12px', fontWeight: '500' }}>
            Difference (Hedged - Outstanding)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#495057' }}>PT:</span>
              <span
                style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: dashboardData.difference.pt >= 0 ? '#2e7d32' : '#dc3545',
                  fontFamily: 'monospace',
                }}
              >
                {formatNumber(dashboardData.difference.pt)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#495057' }}>PD:</span>
              <span
                style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: dashboardData.difference.pd >= 0 ? '#2e7d32' : '#dc3545',
                  fontFamily: 'monospace',
                }}
              >
                {formatNumber(dashboardData.difference.pd)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#495057' }}>RH:</span>
              <span
                style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: dashboardData.difference.rh >= 0 ? '#2e7d32' : '#dc3545',
                  fontFamily: 'monospace',
                }}
              >
                {formatNumber(dashboardData.difference.rh)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Hedges List - Card Based Design */}
      {!isAddPopupShown && !isEditPopupShown && (
        <div>
          <div className="hedge-contracts-header">
            <h3 className="hedge-contracts-title">Hedge contracts</h3>
            <span className="hedge-contracts-count">
              {hedgeItems.filter((h) => h.isActive).length} active
              {showInactive ? ` · ${hedgeItems.filter((h) => !h.isActive).length} inactive` : ''}
            </span>
          </div>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>Loading hedges...</div>
          ) : hedgeItems.length === 0 ? (
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                padding: '40px',
                textAlign: 'center',
                color: '#6c757d',
                border: '1px solid #e9ecef',
              }}
            >
              No {showInactive ? '' : 'active '}hedge contracts found. Click "Add Hedge" to create one.
            </div>
          ) : (
            <div className="hedge-contracts-grid">
              {hedgeItems.map((hedge) => (
                <div
                  key={hedge.hedgeId}
                  className={`hedge-contract-card${hedge.isActive ? '' : ' hedge-contract-card--inactive'}`}
                >
                  {/* Header */}
                  <div className="hedge-contract-header">
                    <div className="hedge-contract-header-row">
                      <span className="hedge-contract-oem">{hedge.oemName}</span>
                      <div className="hedge-contract-actions">
                        {hedge.isActive && (
                          <button
                            type="button"
                            onClick={() => handleEdit(hedge)}
                            className="hedge-card-btn hedge-card-btn--edit"
                            title="Edit contract"
                          >
                            Edit
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleToggleStatus(hedge)}
                          className={`hedge-card-btn ${hedge.isActive ? 'hedge-card-btn--deactivate' : 'hedge-card-btn--activate'}`}
                          title={hedge.isActive ? 'Deactivate contract' : 'Activate contract'}
                        >
                          {hedge.isActive ? 'Disable' : 'Enable'}
                        </button>
                      </div>
                    </div>
                    <div className="hedge-contract-date">
                      <span className={`hedge-status-badge${hedge.isActive ? ' hedge-status-badge--active' : ' hedge-status-badge--inactive'}`}>
                        {hedge.isActive ? 'Active' : 'Inactive'}
                      </span>
                      <span className="hedge-contract-date-text">{hedge.hedgeDateDisplay}</span>
                    </div>
                  </div>

                  {/* Metal Ounces */}
                  <div className="hedge-metals-grid">
                    {renderMetalBlock('Platinum (PT)', hedge.ptOuncesDisplay, hedge.ptPricePerOzDisplay, hedge.ptTotalValue)}
                    {renderMetalBlock('Palladium (PD)', hedge.pdOuncesDisplay, hedge.pdPricePerOzDisplay, hedge.pdTotalValue)}
                    {renderMetalBlock('Rhodium (RH)', hedge.rhOuncesDisplay, hedge.rhPricePerOzDisplay, hedge.rhTotalValue)}
                  </div>

                  {hedge.grandTotalValue && parseFloat(hedge.grandTotalValue) > 0 && (
                    <div className="hedge-total-row">
                      <span className="hedge-total-label">Total Contract Value:</span>
                      <span className="hedge-total-value">${formatCurrency(hedge.grandTotalValue)}</span>
                    </div>
                  )}

                  {hedge.notes && (
                    <div className="hedge-notes">
                      <div className="hedge-notes-label">Notes</div>
                      <div className="hedge-notes-text">{hedge.notes}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {(isAddPopupShown || isEditPopupShown) && (
        <Modal
          onClose={handleClose}
          title={isAddPopupShown ? 'Add Hedge' : 'Edit Hedge'}
        >
          <HedgeAddEdit
            item={selectedHedge}
            onClose={handleClose}
            onSave={handleSave}
            type={isAddPopupShown ? 'add' : 'edit'}
            isLoading={isLoading}
          />
        </Modal>
      )}

      </div>

      {/* Toast */}
      {toastParams.message && (
        <Toast
          type={toastParams.type}
          message={toastParams.message}
          id={toastParams.id}
        />
      )}
    </PageLayout>
  );
};

export default Hedge;
